import { EXTRACT_OCR_STATUS } from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { ExtractOCRStatusResult, FileState, Patient } from "core/types";
import { createContext, ReactNode, useContext, useMemo } from "react";
import { useExtractOCR } from "./hooks/extract/ocr";
import { useExtractOCRStatus } from "./hooks/extract/status";

export type ExtractDocsContextValue = {
  extractOcr: ReturnType<typeof useExtractOCR>;
  extractOcrStatus: ReturnType<typeof useExtractOCRStatus>;
  utils: {
    getExtractedDocument: (
      fileId: FileState["id"],
    ) => ExtractOCRStatusResult | undefined;
    isFileExtracted: (fileId: FileState["id"]) => boolean;
    isFileExtractionFailed: (fileId: FileState["id"]) => boolean;
    isFileExtractionProcessing: (fileId: FileState["id"]) => boolean;
    isFileExtractionPending: (fileId: FileState["id"]) => boolean;
  };
};

const emptyFunction = (functionName: string) => () => {
  throw new Error(`${functionName} must be used within an ExtractDocsProvider`);
};

const emptyExtractDocsContextValue: ExtractDocsContextValue = {
  extractOcr: {
    errors: [],
    extractingAllFiles: false,
    handleDownloadAndRunOcr: emptyFunction("handleDownloadAndRunOcr"),
    handleOcrForFile: emptyFunction("handleOcrForFile"),
    processingFileIds: [],
    results: {},
  },
  extractOcrStatus: {
    error: null,
    refetch: emptyFunction("refetch"),
    result: null,
  },
  utils: {
    getExtractedDocument: emptyFunction("getExtractedDocument"),
    isFileExtracted: emptyFunction("isFileExtracted"),
    isFileExtractionFailed: emptyFunction("isFileExtractionFailed"),
    isFileExtractionPending: emptyFunction("isFileExtractionPending"),
    isFileExtractionProcessing: emptyFunction("isFileExtractionProcessing"),
  },
};

const ExtractDocsContext = createContext<ExtractDocsContextValue | undefined>(
  undefined,
);

type ExtractDocsProviderProps = {
  children: ReactNode;
  patient: Patient;
};

export const ExtractDocsProvider = ({
  children,
  patient,
}: ExtractDocsProviderProps) => {
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const extractOcrStatus = useExtractOCRStatus({
    careseekerId: patient.careseeker?.id,
    patientId: patient.id,
    skip: !isDocsAndExtractActive && !isDischargeLetterActive,
  });
  const extractOcr = useExtractOCR({
    careseekerId: patient.careseeker?.id,
    externalId: patient.external_id,
    ocrStatus: extractOcrStatus.result,
    onComplete: extractOcrStatus.refetch,
    patientId: patient.id,
  });

  const getExtractedDocument = (
    fileId: FileState["id"],
  ): ExtractOCRStatusResult | undefined => {
    return extractOcrStatus.result?.documents.find(
      (doc) => doc.file_id === String(fileId),
    );
  };

  const isFileExtracted = (fileId: FileState["id"]): boolean => {
    return (
      getExtractedDocument(fileId)?.status === EXTRACT_OCR_STATUS.EXTRACTED
    );
  };

  const isFileExtractionProcessing = (fileId: FileState["id"]): boolean => {
    return (
      getExtractedDocument(fileId)?.status === EXTRACT_OCR_STATUS.PROCESSING ||
      extractOcr.processingFileIds.includes(fileId)
    );
  };

  const isFileExtractionFailed = (fileId: FileState["id"]): boolean => {
    return getExtractedDocument(fileId)?.status === EXTRACT_OCR_STATUS.FAILED;
  };

  const isFileExtractionPending = (fileId: FileState["id"]): boolean => {
    return (
      !getExtractedDocument(fileId) ||
      getExtractedDocument(fileId)?.status === EXTRACT_OCR_STATUS.PENDING
    );
  };

  const utils = {
    getExtractedDocument,
    isFileExtracted,
    isFileExtractionFailed,
    isFileExtractionPending,
    isFileExtractionProcessing,
  };

  const value: ExtractDocsContextValue = useMemo(
    () => ({
      extractOcr,
      extractOcrStatus,
      utils,
    }),
    [extractOcr, extractOcrStatus, utils],
  );

  return (
    <ExtractDocsContext.Provider value={value}>
      {children}
    </ExtractDocsContext.Provider>
  );
};

export const useExtractDocs = (): ExtractDocsContextValue => {
  const context = useContext(ExtractDocsContext);
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  if (!isDocsAndExtractActive && !isDischargeLetterActive) {
    return emptyExtractDocsContextValue;
  }

  if (context === undefined) {
    throw new Error(
      "useExtractDocs must be used within an ExtractDocsProvider",
    );
  }

  return context;
};
